import ReactEChartsCore from 'echarts-for-react/lib/core';
import { ScatterChart } from 'echarts/charts';
import {
  GridComponent,
  LegendComponent,
  TooltipComponent
} from 'echarts/components';
import * as echarts from 'echarts/core';
import type { EChartsOption } from 'echarts';
import {
  breakpointsMediaQuery,
  getColor,
  getRandomColor,
  useMediaQuery
} from '@/helpers/utils';
import { Card } from 'react-bootstrap';
import FalconCardHeader from '@/components/common/FalconCardHeader';
import HelpTooltip from '@/app/common/components/HelpTooltip';
import { useRef } from 'react';
import CardChartDropDown from '@/components/common/CardChartDropDown';
import numberHumanizer from '@/helpers/numberHumanizer';
import { serpTooltips } from '@/app/Serp/tooltip/serpTooltips';
import { useTooltipHide } from '@/hooks/useTooltipHide';
import type { SerpAnalysisDomains } from '@/app/Serp/types/serpApiTypes';
echarts.use([ScatterChart, TooltipComponent, GridComponent, LegendComponent]);

interface DomainAuthoritySpamScoreProps {
  data?: SerpAnalysisDomains['spam_authority_visits'];
  embedded?: boolean;
}

const DomainAuthoritySpamScore = ({
  data,
  embedded = false
}: DomainAuthoritySpamScoreProps) => {
  const chartRef = useRef<ReactEChartsCore | null>(null);
  const isMdUp = useMediaQuery(breakpointsMediaQuery.md);
  useTooltipHide(chartRef);

  const cardRef = useRef<HTMLDivElement | null>(null);
  //   const formattedBar = data?.categories?.map(row => row);
  const formattedData = data?.map(
    ({ x: spam, y: authority, z: visits, name }) => ({
      value: [spam, authority, visits, name],
      name,
      itemStyle: { color: getColor(getRandomColor()) }
    })
  );

  // تنظیمات نمودار
  const getOption = (): EChartsOption => ({
    grid: { right: '5%', left: '10%', bottom: '12%', top: '5%' },
    textStyle: {
      fontFamily: 'dana'
    },
    tooltip: {
      triggerOn: isMdUp ? 'mousemove' : 'click',
      trigger: 'item',
      padding: [7, 10],
      backgroundColor: getColor('gray-100'),
      borderColor: getColor('gray-300'),
      textStyle: { color: getColor('gray-1100') },
      borderWidth: 1,
      formatter: (params: any) => {
        return `<div style="font-weight:bold;">${params?.value[3]}</div></br>
        <div class='d-flex flex-column  flex-md-row'>اسپم اسکور:&nbsp; <span style="font-weight:bold;">${params?.value[0]}</span></div>
        <div class='d-flex flex-column  flex-md-row'>دامین آتوریتی:&nbsp; <span style="font-weight:bold;">${params?.value[1]}</span></div>
        <div class='d-flex flex-column  flex-md-row'>میزان بازدید:&nbsp; <span style="font-weight:bold;">${numberHumanizer(params?.value[2])}</span></div>

        `.toPersianDigit();
      },
      transitionDuration: 0,
      axisPointer: {
        // type: 'shadow'
      }
    },
    xAxis: {
      name: 'اسپم اسکور',
      nameLocation: 'center',
      nameTextStyle: { color: getColor('gray-900'), padding: 10 },
      boundaryGap: ['9%', '9%'],
      axisLine: {
        lineStyle: {
          color: getColor('gray-300'),
          type: 'solid'
        }
      },
      axisLabel: {
        show: true,
        color: getColor('gray-800'),
        margin: 5
      },
      splitLine: {
        show: true,
        lineStyle: {
          color: getColor('gray-200')
        }
      }
    },
    yAxis: {
      name: 'دامین آتوریتی',
      nameLocation: 'center',
      nameTextStyle: { color: getColor('gray-900'), padding: 24 },
      boundaryGap: ['20%', '10%'],
      axisLine: {
        lineStyle: {
          color: getColor('gray-300'),
          type: 'solid'
        }
      },
      axisLabel: {
        show: true,
        color: getColor('gray-800'),
        margin: 5
      },
      splitLine: {
        show: true,
        lineStyle: {
          color: getColor('gray-200')
        }
      }
    },
    series: [
      {
        symbolSize: function (data: number[]) {
          const minSize = 10;
          const maxSize = 80;
          const zValues =
            formattedData?.map(item => Number(item.value[2]) || 0) ?? [];

          if (!zValues.length) return minSize;

          const minZ = Math.min(...zValues);
          const maxZ = Math.max(...zValues);

          // وقتی فقط یک نقطه باشد یا همه z برابر باشند (مثلاً 0)، تقسیم بر صفر NaN می‌دهد
          if (maxZ === minZ) return (minSize + maxSize) / 2;

          return (
            ((Number(data[2]) - minZ) / (maxZ - minZ)) * (maxSize - minSize) +
            minSize
          );
        },
        data: formattedData as any,
        type: 'scatter'
      }
    ]
  }) as unknown as EChartsOption;

  const chart = (
    <ReactEChartsCore
      ref={chartRef}
      echarts={echarts}
      option={getOption()}
      style={{ height: '23rem' }}
    />
  );

  if (embedded) return chart;

  return (
    <Card className="mb-3" ref={cardRef}>
      <FalconCardHeader
        title={
          <div>
            اسپم اسکور و دامین آتوریتی و بازدید سایت{' '}
            <HelpTooltip html={serpTooltips.spamAuthorityVisit} />
          </div>
        }
        titleTag="h6"
        className="py-2"
        light
        endEl={<CardChartDropDown cardRef={cardRef} chartRef={chartRef} />}
      />
      <Card.Body className="px-3">{chart}</Card.Body>
    </Card>
  );
};
export default DomainAuthoritySpamScore;
